<template>
  <div class="flex flex-col h-full bg-white lg:bg-blue-5">
    <slot
      v-if="$slots.banner"
      name="banner"
    />
    <slot name="header" />

    <article
      class="flex-1 w-full max-w-8xxl mx-auto grid"
      :class="[
        component.wrapper,
        classObj?.wrapper,
        {
          'grid-rows-[auto,auto,1fr] lg:grid-rows-none': !!$slots['sidebar-right'],
          'grid-rows-[auto,1fr] lg:grid-rows-none': !$slots['sidebar-right'],
        }
      ]"
    >
      <!-- eslint-disable vue/no-v-html -->
      <aside
        class="bg-white lg:bg-transparent order-1 shadow-sm-1 lg:shadow-none relative z-2"
        :class="[
          component.left,
          classObj?.left
        ]"
      >
        <slot name="sidebar-left" />
      </aside>

      <div
        class="h-full order-3 lg:order-2"
        :class="[
          component.main,
          classObj?.main
        ]"
      >
        <slot name="main" />
      </div>

      <aside
        v-if="!!$slots['sidebar-right']"
        class="flex flex-col gap-6 order-2 lg:order-3"
        :class="[
          component.right,
          classObj?.right
        ]"
      >
        <slot name="sidebar-right" />
      </aside>
    </article>
  </div>
</template>

<script setup>
defineOptions({
  name: 'LayoutsFormFunnel'
})

const props = defineProps({
  header: {
    type: Object,
    default: () => ({})
  },

  stepLabel: {
    type: String,
    default: ''
  },

  layout: {
    type: String,
    default: 'default',
    validator: value => ['default', 'comparison', 'application'].includes(value)
  },

  classObj: {
    type: Object,
    default: () => ({})
  }
})

const layoutStore = useLayoutsStore()

onMounted(() => {
  layoutStore.navigation.layout = 'minimal'
  layoutStore.navigation.hasShadow = true
  layoutStore.navigation.hasLoginPopup = true
})

onBeforeUnmount(() => {
  layoutStore.navigation.layout = 'default'
  layoutStore.navigation.hasShadow = false
  layoutStore.navigation.hasLoginPopup = false
})

const component = computed(() => {
  const wrapper = (() => {
    const grid = getKey(props.layout, {
      default: 'lg:grid-cols-[7.9375rem,1fr] xl:grid-cols-[7.9375rem,1fr,22.3125rem]',
      comparison: 'lg:grid-cols-[162px,1fr] xl:grid-cols-[162px,1fr,357px]',
      application: 'lg:grid-cols-[127px,1fr] xl:grid-cols-[127px,1fr,472px]'
    })

    const bgColor = getKey(props.layout, {
      default: 'lg:bg-blue-5',
      comparison: 'lg:bg-gray-10'
    })

    return `${grid} ${bgColor}`
  })()

  const left = getKey(props.layout, {
    default: 'p-4 lg:pr-4 lg:py-14 lg:mb-0',
    // application: 'lg:block md:hidden',
    comparison: 'sm:p-4 lg:pt-6 lg:px-8'
  })

  const main = getKey(props.layout, {
    default: 'bg-white px-4 pt-6 pb-10 lg:px-10 lg:pt-10 lg:pb-20',
    application: 'bg-white p-4 lg:px-9 lg:pt-10 lg:pb-[5.75rem]',
    comparison: 'bg-gray-10 p-4 pb-[4.25rem] lg:px-0 lg:pt-6 lg:pr-4 xl:pr-0'
  })

  const right = getKey(props.layout, {
    default: 'pl-6 pr-12 pt-16 pb-4',
    application: 'lg:pl-10 lg:pr-20 lg:py-10',
    comparison: 'lg:pl-9 lg:pr-12 lg:py-6'
  })

  return {
    wrapper,
    left,
    main,
    right
  }
})
</script>
